/**
 * useMicroTransactions — fetches the micro-transactions captured for a time
 * entry (transaction) from the micro-link API. The backend traverses the
 * entry's reverse lineage down to every leaf source entry and aggregates the
 * micros from all of them, so for a group/merge/collapse result this spans
 * multiple source entries across multiple dates (backend PR #286).
 *
 * `transactionId` is the raw numeric transaction id. Manual entries have no
 * captured activity and the backend returns an empty array.
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useApi } from "@/shared/utils/request";

export const microSummarySchema = z.object({
	id: z.string(),
	date: z.string(), // YYYY-MM-DD
	app_id: z.number().nullable(),
	process_name: z.string(),
	title: z.string().nullable(),
	doc_path: z.string().nullable(),
	payload_size_kb: z.number().nullable(),
	received_at: z.string(), // ISO datetime
});

export const microSummaryArraySchema = z.array(microSummarySchema);

export type MicroSummary = z.infer<typeof microSummarySchema>;

export function useMicroTransactions(transactionId: string) {
	const { makeApiRequest } = useApi();

	const query = useQuery({
		queryKey: ["internal", "micro-transactions", transactionId] as const,
		enabled: !!transactionId,
		queryFn: async ({ signal }) => {
			const response = await makeApiRequest({
				endpoint: `/transactions/${transactionId}/micro-transactions`,
				method: "GET",
				otherOptions: { signal },
			});
			if (response.error) throw new Error(response.error);
			return microSummaryArraySchema.parse(response.data ?? []);
		},
	});

	return {
		micros: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error?.message ?? null,
	};
}
